/**
 * AttemptsTable — unified attempts list matching sandbox reports.php.
 *
 * Columns (sandbox order): Candidate · Test · Company · Date · Duration ·
 * Score · Result · [View action].
 *
 * Column visibility is driven by the `role` prop:
 *   - candidate: Test, Date, Duration, Score, Result (no Candidate, no Company)
 *   - manager:   Candidate, Test, Date, Duration, Score, Result (no Company)
 *   - admin:     all columns
 *
 * Issue 15-frontend-gaps/15.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';
import type { AttemptsTableRole, AttemptsTableRow } from './types.ts';

export type { AttemptsTableRole, AttemptsTableRow } from './types.ts';

export interface AttemptsTableProps {
  role: AttemptsTableRole;
  rows: AttemptsTableRow[];
  loading: boolean;
  loadingMore?: boolean;
  /** Cursor pagination — if present, a "Load more" button is rendered. */
  onLoadMore?: () => void;
  hasMore?: boolean;
  /** Error message to display. */
  error?: string | null;
  /** Retry callback when error is shown. */
  onRetry?: () => void;
  /** Empty-state configuration. */
  emptyState?: {
    ctaLabel: string;
    ctaHref: string;
  };
  /** Escape-to-nav fallback href for error state. */
  errorFallbackHref?: string;
}

/** Column key — controls visibility per role. */
type ColKey =
  | 'candidate'
  | 'test'
  | 'company'
  | 'date'
  | 'duration'
  | 'score'
  | 'result'
  | 'action';

const ROLE_COLUMNS: Record<AttemptsTableRole, ColKey[]> = {
  candidate: ['test', 'date', 'duration', 'score', 'result', 'action'],
  manager: [
    'candidate',
    'test',
    'date',
    'duration',
    'score',
    'result',
    'action',
  ],
  admin: [
    'candidate',
    'test',
    'company',
    'date',
    'duration',
    'score',
    'result',
    'action',
  ],
};

const COLUMN_LABELS: Record<ColKey, string> = {
  candidate: 'Candidate',
  test: 'Test',
  company: 'Company',
  date: 'Date',
  duration: 'Duration',
  score: 'Score',
  result: 'Result',
  action: '',
};

/** Format seconds → "Xm Ys" or "—" if null. */
function formatDuration(seconds: number | null): string {
  if (seconds === null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

/** Format ISO date → short display. */
function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/** Score bar fill color class based on percentage. */
function scoreBarColor(pct: number): string {
  if (pct < 60) return 'bg-red-500';
  if (pct < 80) return 'bg-amber-500';
  return 'bg-green-600';
}

export function AttemptsTable({
  role,
  rows,
  loading,
  loadingMore = false,
  onLoadMore,
  hasMore = false,
  error,
  onRetry,
  emptyState,
  errorFallbackHref = '/',
}: AttemptsTableProps): ReactElement {
  const columns = ROLE_COLUMNS[role];

  // Loading state — skeleton rows
  if (loading && rows.length === 0) {
    return (
      <div
        className="rounded-lg border border-line bg-white overflow-hidden"
        aria-busy="true"
        aria-live="polite"
      >
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line">
              {columns.map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider text-warning"
                >
                  {COLUMN_LABELS[col]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: 3 }).map((_, i) => (
              <tr key={i} className="border-b border-line">
                {columns.map((col) => (
                  <td key={col} className="px-4 py-3">
                    <div className="h-4 w-24 animate-pulse rounded bg-card" />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  // Error state with retry + fallback
  if (error) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-line bg-white p-6"
      >
        <p className="mb-4 font-medium text-error">{error}</p>
        <div className="flex items-center gap-3">
          {onRetry && (
            <button
              type="button"
              className="rounded-md border border-line px-4 py-2 text-sm hover:bg-bg-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
              onClick={onRetry}
            >
              Retry
            </button>
          )}
          <Link
            to={errorFallbackHref}
            className="text-sm text-accent hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Return to dashboard
          </Link>
        </div>
      </div>
    );
  }

  // Empty state
  if (rows.length === 0 && !loading) {
    return (
      <section className="rounded-lg border border-line bg-white p-6">
        <h2 className="font-sans text-xl font-medium">No attempts yet</h2>
        <p className="mt-2 text-muted">
          {role === 'candidate'
            ? 'Start a test from the catalogue to see your history here.'
            : role === 'manager'
              ? 'Attempts will appear here once candidates begin tests.'
              : 'No attempts match the current filters.'}
        </p>
        {emptyState && (
          <div className="mt-4">
            <Link
              to={emptyState.ctaHref}
              className="inline-flex rounded-md border border-line px-4 py-2 text-sm font-medium hover:bg-bg-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            >
              {emptyState.ctaLabel}
            </Link>
          </div>
        )}
      </section>
    );
  }

  // Main table
  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-lg border border-line bg-white">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-line">
              {columns.map((col) => (
                <th
                  key={col}
                  scope="col"
                  className="px-4 py-3 text-left font-mono text-xs font-semibold uppercase tracking-wider text-warning"
                >
                  {COLUMN_LABELS[col]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.attempt_id}
                className="border-b border-line transition-colors hover:bg-accent-l"
              >
                {columns.map((col) => (
                  <td key={col} className="px-4 py-3 text-sm">
                    <CellContent col={col} row={row} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Load more */}
      {hasMore && (
        <div className="flex justify-center">
          <button
            type="button"
            className="rounded-md border border-line px-4 py-2 text-sm font-medium hover:bg-bg-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 disabled:opacity-50"
            disabled={loadingMore}
            onClick={onLoadMore}
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cell rendering per column
// ---------------------------------------------------------------------------

function CellContent({
  col,
  row,
}: {
  col: ColKey;
  row: AttemptsTableRow;
}): ReactElement {
  switch (col) {
    case 'candidate':
      return (
        <div>
          <div className="font-semibold text-ink">
            {row.candidate_name}
          </div>
          <div className="font-mono text-xs text-muted">
            {row.candidate_email}
          </div>
        </div>
      );

    case 'test':
      return <span className="text-ink">{row.test_title}</span>;

    case 'company':
      return row.company_name ? (
        <span className="inline-flex rounded-pill bg-accent-l px-2 py-0.5 text-xs font-medium text-accent">
          {row.company_name}
        </span>
      ) : (
        <span className="inline-flex rounded-pill bg-card px-2 py-0.5 text-xs font-medium text-muted">
          Individual
        </span>
      );

    case 'date':
      return (
        <span className="font-mono text-xs text-muted">
          {formatDate(row.date)}
        </span>
      );

    case 'duration':
      return (
        <span className="font-mono text-xs text-muted">
          {formatDuration(row.duration_seconds)}
        </span>
      );

    case 'score':
      return row.score_percentage !== null ? (
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-20 overflow-hidden rounded-pill bg-bg-warm">
            <div
              className={`h-full rounded-pill ${scoreBarColor(row.score_percentage)}`}
              style={{ width: `${row.score_percentage}%` }}
            />
          </div>
          <span className="font-semibold">{row.score_percentage}%</span>
        </div>
      ) : (
        <span className="text-muted">—</span>
      );

    case 'result':
      return row.passed ? (
        <span className="inline-flex rounded-pill bg-success-l px-2 py-0.5 text-xs font-semibold text-success">
          ✓ Pass
        </span>
      ) : (
        <span className="inline-flex rounded-pill bg-error-l px-2 py-0.5 text-xs font-semibold text-error">
          ✗ Fail
        </span>
      );

    case 'action':
      return (
        <Link
          to={`/attempts/${row.attempt_id}/result`}
          className="inline-flex items-center rounded-md border border-line px-2.5 py-1 text-xs font-medium text-ink hover:bg-bg-warm"
        >
          View
        </Link>
      );
  }
}
